import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Space, Switch, InputNumber, message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getSharedViewById,
  createSharedView,
  updateSharedView,
  SHARED_VIEW_MODULES,
} from '@/api/collaborationApi';
import { useAuth } from '@/hooks/useAuth';

export default function SharedViewForm() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEdit = !!id;
  const canWrite = hasPermission('collaboration', 'write');

  useEffect(() => {
    if (!canWrite) navigate('/collaboration/shared-views');
  }, [canWrite, navigate]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await getSharedViewById(id);
        if (res.data?.success && res.data.data) {
          form.setFieldsValue(res.data.data);
        }
      } catch {
        message.error(t('app.error'));
      }
    })();
  }, [id, form, t]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      if (isEdit) {
        await updateSharedView(id!, values);
        message.success(t('collaboration.shared_view.update_success'));
      } else {
        await createSharedView(values);
        message.success(t('collaboration.shared_view.create_success'));
      }
      navigate('/collaboration/shared-views');
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          sort_descending: true,
          page_size: 20,
          is_default: false,
          is_shared: false,
        }}
      >
        <Form.Item
          name="name"
          label={t('collaboration.shared_view.field.name')}
          rules={[{ required: true, max: 200 }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('collaboration.shared_view.field.description')}
          rules={[{ max: 1000 }]}
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        {!isEdit && (
          <Form.Item
            name="module"
            label={t('collaboration.shared_view.field.module')}
            rules={[{ required: true }]}
          >
            <Select
              options={SHARED_VIEW_MODULES.map((m) => ({
                value: m,
                label: t(`nav.${m}`, m),
              }))}
            />
          </Form.Item>
        )}

        <Form.Item
          name="columns"
          label={t('collaboration.shared_view.field.columns')}
        >
          <Select mode="tags" tokenSeparators={[',']} placeholder={t('collaboration.shared_view.columns_placeholder')} />
        </Form.Item>

        <Form.Item
          name="sort_field"
          label={t('collaboration.shared_view.field.sort_field')}
        >
          <Input />
        </Form.Item>

        <Space>
          <Form.Item
            name="sort_descending"
            label={t('collaboration.shared_view.field.sort_descending')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="page_size"
            label={t('collaboration.shared_view.field.page_size')}
          >
            <InputNumber min={1} max={200} />
          </Form.Item>
        </Space>

        <Space>
          <Form.Item
            name="is_default"
            label={t('collaboration.shared_view.field.is_default')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="is_shared"
            label={t('collaboration.shared_view.field.is_shared')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Space>

        <Form.Item
          name="shared_with_users"
          label={t('collaboration.shared_view.field.shared_with_users')}
        >
          <Select mode="tags" tokenSeparators={[',']} placeholder={t('collaboration.shared_view.users_placeholder')} />
        </Form.Item>

        <Form.Item
          name="shared_with_roles"
          label={t('collaboration.shared_view.field.shared_with_roles')}
        >
          <Select mode="tags" tokenSeparators={[',']} placeholder={t('collaboration.shared_view.roles_placeholder')} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEdit ? t('app.save') : t('app.create')}
            </Button>
            <Button onClick={() => navigate('/collaboration/shared-views')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
